import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import { DateWrapper } from "./DateWrapper";
import SearchWrapper from "./SearchWrapper";
import { featuredRegions } from "../../../utils/constants";
import { translateMonth } from "../../../utils/utils";
import useQueryParams from "../../../customHooks/useQueryParams";

export const FilterMobile = ({
  selectedDates,
  handleSelectDates,
  searchMobile,
  handleCloseSearchMobile,
  handleSelectMonths,
  handleSearch,
  handleClearSelections,
  dates,
  search,
  onChangeSearch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useQueryParams();

  const weekParam = params.get("week");
  const monthParam = params.get("months");
  const yearParam = params.get("year");
  const regionParam = params.get("region");
  const subregionParam = params.get("subregion");
  const destinationParam = params.get("destination");

  // Keep in sync with the header pill's label fallback chain in header.js.
  const currentLocationLabel = regionParam
    ? t(`region_${regionParam}`, { defaultValue: regionParam })
    : subregionParam
    ? t(`subregion_${subregionParam.replace(/-/g, "_")}`, { defaultValue: subregionParam })
    : destinationParam
    ? t(`destinations.${destinationParam.toLowerCase()}`, { defaultValue: destinationParam })
    : null;

  // Helper to get date display text with week range if applicable
  const getDateDisplayText = (monthNum) => {
    if (weekParam && monthParam && !monthParam.includes(",")) {
      const weekNum = parseInt(weekParam, 10);
      const month = parseInt(monthParam, 10);
      const year = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();
      const daysInMonth = new Date(year, month, 0).getDate();

      let startDay, endDay;
      if (weekNum === 1) {
        startDay = 1;
        endDay = 7;
      } else if (weekNum === 2) {
        startDay = 8;
        endDay = 14;
      } else if (weekNum === 3) {
        startDay = 15;
        endDay = 21;
      } else if (weekNum === 4) {
        startDay = 22;
        endDay = 28;
      } else {
        startDay = 29;
        endDay = daysInMonth;
      }

      const monthName = translateMonth(monthNum);
      return `${startDay}-${endDay} ${monthName}`;
    }
    return translateMonth(monthNum);
  };

  // If user already picked a region/subregion/destination, default to the date
  // step so they can refine immediately instead of re-selecting their location.
  const [currentFocus, setCurrentFocus] = useState(currentLocationLabel ? "when" : "where");
  const [openDeepSearch, setOpenDeepSearch] = useState(false);
  const searchRef = useRef(null);

  if (!searchMobile) return null;

  return createPortal(
    <AnimatePresence>
      {searchMobile && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="tw-fixed tw-inset-0 tw-z-[10000] tw-flex tw-flex-col"
          style={{ background: "#f7f7f7" }}
        >
          {/* Top bar with close button */}
          <div className="tw-flex tw-items-center tw-justify-start tw-px-4 tw-pt-4 tw-pb-2">
            <button
              type="button"
              onClick={handleCloseSearchMobile}
              className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-white"
              style={{
                border: "1px solid #e5e7eb",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="#222"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M1 1l10 10M11 1L1 11" />
              </svg>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-28">
            {/* WHERE card */}
            <div
              className="tw-bg-white tw-rounded-3xl tw-mb-3 tw-overflow-hidden"
              style={{
                boxShadow:
                  currentFocus === "where"
                    ? "0 4px 16px rgba(0,0,0,0.12)"
                    : "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              {currentFocus === "where" ? (
                <div className="tw-p-6">
                  <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4">
                    {t("where_to", { defaultValue: "Where to?" })}
                  </h2>

                  {/* Search input */}
                  <button
                    type="button"
                    className="tw-flex tw-items-center tw-gap-3 tw-w-full tw-rounded-2xl tw-px-4 tw-py-3 tw-text-left tw-bg-white"
                    style={{
                      border: "1px solid #d1d5db",
                    }}
                    onClick={() => {
                      setOpenDeepSearch(true);
                      setTimeout(() => {
                        searchRef.current?.focus();
                      }, 100);
                    }}
                  >
                    <SearchOutlinedIcon
                      style={{ fontSize: 20, color: "#222" }}
                    />
                    <span
                      className={`tw-text-sm ${
                        search ? "tw-text-gray-900" : "tw-text-gray-400"
                      }`}
                    >
                      {search ||
                        t("search_destinations", {
                          defaultValue: "Search destinations",
                        })}
                    </span>
                  </button>

                  {/* Region list */}
                  <div className="tw-mt-5">
                    <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">
                      {t("explore_by_region")}
                    </p>
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      {featuredRegions
                        .slice(0, 4)
                        .map(([region, image, translationKey], key) => {
                          const isActive = region === regionParam;
                          return (
                          <button
                            type="button"
                            onClick={() => {
                              navigate({
                                pathname: "/all-trips",
                                search: `?region=${region}`,
                              });
                              handleCloseSearchMobile();
                            }}
                            key={key}
                            className="tw-flex tw-items-center tw-gap-3 tw-py-2 tw-px-2 tw-rounded-xl tw-text-left"
                            style={
                              isActive
                                ? { border: "1px solid #222", background: "#f7f7f7" }
                                : { border: "1px solid transparent" }
                            }
                          >
                            <div
                              className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-gray-100 tw-shrink-0 tw-overflow-hidden"
                              style={{
                                backgroundImage: `url('${image}')`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }}
                            />
                            <div>
                              <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                                {t(`${translationKey}`)}
                              </p>
                              <p className="tw-text-xs tw-text-gray-400">
                                {t("explore_trips", {
                                  defaultValue: "Explore trips",
                                })}
                              </p>
                            </div>
                          </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="tw-flex tw-items-center tw-justify-between tw-w-full tw-px-6 tw-py-4"
                  onClick={() => setCurrentFocus("where")}
                >
                  <span className="tw-text-sm tw-font-medium tw-text-gray-500">
                    {t("where_label", { defaultValue: "Where" })}
                  </span>
                  <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                    {search ||
                      currentLocationLabel ||
                      t("flexible", {
                        defaultValue: "I'm flexible",
                      })}
                  </span>
                </button>
              )}
            </div>

            {/* WHEN card */}
            <div
              className="tw-bg-white tw-rounded-3xl tw-mb-3 tw-overflow-hidden"
              style={{
                boxShadow:
                  currentFocus === "when"
                    ? "0 4px 16px rgba(0,0,0,0.12)"
                    : "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              {currentFocus === "when" ? (
                <div className="tw-p-6">
                  <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
                    {t("when_trip", { defaultValue: "When's your trip?" })}
                  </h2>
                  <DateWrapper
                    selectedDates={selectedDates}
                    handleSelectDates={handleSelectDates}
                    handleSelectMonths={handleSelectMonths}
                    dates={dates}
                    hideTitle
                    useTailwind
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className="tw-flex tw-items-center tw-justify-between tw-w-full tw-px-6 tw-py-4"
                  onClick={() => setCurrentFocus("when")}
                >
                  <span className="tw-text-sm tw-font-medium tw-text-gray-500">
                    {t("when", { defaultValue: "When" })}
                  </span>
                  <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                    {selectedDates.length === 1
                      ? getDateDisplayText(selectedDates[0])
                      : selectedDates.length > 1
                      ? t("date_and_more", {
                          date: getDateDisplayText(selectedDates[0]),
                        })
                      : t("any_month", { defaultValue: "Any month" })}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Deep search portal */}
          <AnimatePresence>
            {openDeepSearch && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="tw-fixed tw-inset-0 tw-z-[10001] tw-flex tw-flex-col tw-bg-white tw-overflow-y-auto"
              >
                <SearchWrapper
                  handleCloseSearchMobile={handleCloseSearchMobile}
                  onOutsideClick={() => setOpenDeepSearch(false)}
                  onChangeSearch={onChangeSearch}
                  onSearch={() => {
                    handleSearch();
                    handleCloseSearchMobile();
                  }}
                  searchQuery={search}
                  searchRef={searchRef}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bottom action bar */}
          <div
            className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-px-5 tw-py-4 tw-flex tw-items-center tw-justify-between"
            style={{
              borderTop: "1px solid #f0f0f0",
              paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
            }}
          >
            <button
              type="button"
              onClick={() => {
                handleClearSelections();
                handleCloseSearchMobile();
                navigate("/all-trips");
              }}
              className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-underline"
            >
              {t("clear_all", { defaultValue: "Clear all" })}
            </button>

            <button
              type="button"
              onClick={() => {
                handleSearch(true);
                handleCloseSearchMobile();
              }}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-px-5 tw-py-3 tw-text-white tw-font-semibold tw-text-sm"
              style={{
                background: "#00bfff",
              }}
            >
              <SearchOutlinedIcon style={{ fontSize: 18 }} />
              {t("search", { defaultValue: "Search" })}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
